import {
  Container,
  Grid,
  Paper,
  Text,
  Stack,
  Group,
  Table,
  ScrollArea,
  ThemeIcon,
} from "@mantine/core";
import { useEffect, useState } from "react";
import { PerformRequest } from "../../../lib/usePerformRequest";
import { Endpoints } from "../../../lib/endpoints";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import { IconChartBar } from "@tabler/icons-react";

import { IAnalyticsStats } from "../../../lib/types/Admin";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8"];

export default function Analytics() {
  const [stats, setStats] = useState<IAnalyticsStats | null>(null);

  useEffect(() => {
    const fetchStats = async () => {
      const res = await PerformRequest<IAnalyticsStats>({
        method: Endpoints.GetVisitorStats().method,
        route: Endpoints.GetVisitorStats().route,
      });
      if (res && res.data) {
        setStats(res.data);
      }
    };
    fetchStats();
  }, []);

  if (!stats) return <Text>Loading analytics...</Text>;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const StatsCard = ({ title, value, icon: Icon, color }: any) => (
    <Paper shadow="xs" p="md" radius="md" withBorder>
      <Group justify="space-between">
        <Stack gap={0}>
          <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
            {title}
          </Text>
          <Text fw={700} size="xl">
            {value}
          </Text>
        </Stack>
        <ThemeIcon size="lg" radius="md" color={color} variant="light">
          <Icon size={20} stroke={1.5} />
        </ThemeIcon>
      </Group>
    </Paper>
  );

  return (
    <Container size="xl">
      <Stack gap="lg">
        <Text size="xl" fw={700}>
          Analytics Dashboard
        </Text>

        {/* Summary Cards */}
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <StatsCard
              title="Total Unique Visitors"
              value={stats.summary.total}
              icon={IconChartBar}
              color="blue"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <StatsCard
              title="Visits Today"
              value={stats.summary.today}
              icon={IconChartBar}
              color="green"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <StatsCard
              title="This Week"
              value={stats.summary.week}
              icon={IconChartBar}
              color="orange"
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <StatsCard
              title="This Month"
              value={stats.summary.month}
              icon={IconChartBar}
              color="grape"
            />
          </Grid.Col>
        </Grid>

        {/* Traffic Chart */}
        <Paper shadow="xs" p="md" radius="md" withBorder>
          <Text size="lg" fw={600} mb="md">
            Traffic Overview (Last 30 Days)
          </Text>
          <div style={{ height: 300, width: "100%" }}>
            <ResponsiveContainer>
              <LineChart data={stats.visitsOverTime}>
                <CartesianGrid strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="date" hide />
                <YAxis />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#228be6"
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Paper>

        <Grid>
          {/* Device Distribution */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper shadow="xs" p="md" radius="md" withBorder h="100%">
              <Text size="lg" fw={600} mb="md">
                Device Distribution
              </Text>
              <div style={{ height: 250, width: "100%" }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={stats.deviceStats}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {stats.deviceStats.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          </Grid.Col>

          {/* Browser Distribution */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper shadow="xs" p="md" radius="md" withBorder h="100%">
              <Text size="lg" fw={600} mb="md">
                Browser Distribution
              </Text>
              <div style={{ height: 250, width: "100%" }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={stats.browserStats}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {stats.browserStats.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          </Grid.Col>

          {/* OS Distribution */}
          <Grid.Col span={{ base: 12, md: 4 }}>
            <Paper shadow="xs" p="md" radius="md" withBorder h="100%">
              <Text size="lg" fw={600} mb="md">
                OS Distribution
              </Text>
              <div style={{ height: 250, width: "100%" }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={stats.osStats}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={80}
                      paddingAngle={5}
                      dataKey="value"
                    >
                      {stats.osStats.map((entry, index) => (
                        <Cell
                          key={index}
                          fill={COLORS[index % COLORS.length]}
                        />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </Paper>
          </Grid.Col>
        </Grid>

        {/* Recent Visits Table */}
        <Paper shadow="xs" p="md" radius="md" withBorder>
          <Text size="lg" fw={600} mb="md">
            Recent Visits
          </Text>
          <ScrollArea h={300}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Date</Table.Th>
                  <Table.Th>Device</Table.Th>
                  <Table.Th>Browser</Table.Th>
                  <Table.Th>OS</Table.Th>
                  {/* <Table.Th>IP</Table.Th>  Privacy: maybe hide IP */}
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {stats.recentVisits.map((visit: any, index: number) => (
                  <Table.Tr key={index}>
                    <Table.Td>{new Date(visit.date).toLocaleString()}</Table.Td>
                    <Table.Td>{visit.device}</Table.Td>
                    <Table.Td>{visit.browser}</Table.Td>
                    <Table.Td>{visit.os}</Table.Td>
                    {/* <Table.Td>{visit.ip}</Table.Td> */}
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        </Paper>
      </Stack>
    </Container>
  );
}
